import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Grid,
  LinearProgress
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PictureAsPdfIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import PDFModalContract from '../components/PDFModalContract';
import { fetchCurrentUser } from '../services/auth';

const ContractsPage = () => {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openPDFModal, setOpenPDFModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [theUser, setCurrentUser] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);
  
  // Filter states
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTypeContrat, setFilterTypeContrat] = useState('all');
  const [filterExpiration, setFilterExpiration] = useState('all');
  const [filterPeriodeEssai, setFilterPeriodeEssai] = useState('all');
  
  const filterOptions = [
    { value: 'all', label: 'Statut: Tous' },
    { value: 'active', label: 'Statut: Actifs' },
    { value: 'inactive', label: 'Statut: Inactifs' },
  ];

  const filterTypeContratOptions = [
    { value: 'all', label: 'Type contrat: Tous' },
    { value: 'CDI – Contrat à Durée Indéterminée', label: 'CDI' },
    { value: 'CDD – Contrat à Durée Déterminée', label: 'CDD' },
    { value: 'Stage', label: 'Stage' },
    { value: 'Intérim', label: 'Intérim' },
    { value: 'Apprentissage', label: 'Apprentissage' },
  ];

  const filterExpirationOptions = [
    { value: 'all', label: 'Contrat expire dans: Tous' },
    { value: '10jours', label: '10 jours' },
    { value: '20jours', label: '20 jours' },
    { value: '30jours', label: '30 jours' },
  ];

  const filterPeriodeEssaiOptions = [
    { value: 'all', label: 'Période essai expire dans: Tous' },
    { value: '10', label: '10 jours' },
    { value: '20', label: '20 jours' },
    { value: '30', label: '30 jours' },
  ];
  const [formData, setFormData] = useState({
    employee_id: '',
    first_name: '',
    last_name: '',
    date_naissance: '',
    lieu_naissance: '',
    adresse: '',
    situation_familiale: '',
    email: '',
    telephone: '',
    poste: '',
    periode_essai_mois: '',
    renouvelable_fois: '',
    duree_preavis_mois: '',
    salaire: '',
    trial_period_end: '',
    type_contrat: '',
    hire_date: '',
    date_fin_contrat: '',
    motif_fin_contrat: '',
    nom_arabic: '',
    prenom_arabic: '',
    adresse_arabic: ''
  });
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Fonction pour calculer la date de fin de période d'essai
  const calculateTrialPeriodEnd = (hireDate, periodeEssaiMois) => {
    if (!hireDate || !periodeEssaiMois || periodeEssaiMois === '0' || periodeEssaiMois === '') {
      return '';
    }
    
    try {
      const startDate = new Date(hireDate);
      if (isNaN(startDate.getTime())) {
        return '';
      }
      
      const months = parseInt(periodeEssaiMois);
      if (isNaN(months) || months <= 0) {
        return '';
      }
      
      // Ajouter le nombre de mois à la date d'embauche
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + months);
      
      // Formater en YYYY-MM-DD
      return endDate.toISOString().split('T')[0];
    } catch (error) {
      console.error('Error calculating trial period end:', error);
      return '';
    }
  };

  // Fonction pour mettre à jour la date de fin de période d'essai
  const updateTrialPeriodEnd = (newFormData) => {
    const calculatedEnd = calculateTrialPeriodEnd(newFormData.hire_date, newFormData.periode_essai_mois);
    return { ...newFormData, trial_period_end: calculatedEnd };
  };

  // Gestionnaire pour le changement de date d'embauche
  const handleHireDateChange = (value) => {
    const newFormData = { ...formData, hire_date: value };
    const updatedFormData = updateTrialPeriodEnd(newFormData);
    setFormData(updatedFormData);
  };

  // Gestionnaire pour le changement de période d'essai
  const handlePeriodeEssaiChange = (value) => {
    const newFormData = { ...formData, periode_essai_mois: value };
    const updatedFormData = updateTrialPeriodEnd(newFormData);
    setFormData(updatedFormData);
  };

  useEffect(() => {
    const loadUser = async () => {
      const currentUser = await fetchCurrentUser();
      if (currentUser) {
        setCurrentUser(currentUser);
      }
      setLoading(false);
    };
    loadUser();
  }, []);

  useEffect(() => {
    fetchContracts();
    fetchEmployees();
  }, []);

  const fetchContracts = async () => {
    try {
      const response = await api.get('/contracts');
      const formattedData = response.data.map(contract => {
        // Déterminer le statut du contrat
        let status = 'Actif';
        if (contract.date_fin_contrat) {
          const endDate = new Date(contract.date_fin_contrat);
          const today = new Date();
          status = endDate < today ? 'Terminé' : 'Actif';
        }
        
        return {
          ...contract,
          employeeName: contract.employee ? `${contract.employee.first_name} ${contract.employee.last_name}` : '............................',
          status: status
        };
      });
      setContracts(formattedData);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter logic
  const filteredContracts = contracts
    .filter((contract) => {
      // Filtre par statut
      if (filterStatus === 'active') {
        return contract.status === 'Actif';
      }
      if (filterStatus === 'inactive') {
        return contract.status === 'Terminé';
      }
      return true;
    })
    .filter((contract) => {
      // Filtre par type de contrat
      if (filterTypeContrat === 'all') {
        return true;
      }
      return contract.type_contrat === filterTypeContrat;
    })
    .filter((contract) => {
      // Filtre par expiration
      if (filterExpiration === 'all') {
        return true;
      }
      
      if (!contract.date_fin_contrat) {
        return false; // Pas de date de fin = pas d'expiration à vérifier
      }
      
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Début de journée
      const endDate = new Date(contract.date_fin_contrat);
      const daysDiff = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
      
      switch (filterExpiration) {
        case '10jours':
          return daysDiff <= 10 && daysDiff > 0;
        case '20jours':
          return daysDiff <= 20 && daysDiff > 0;
        case '30jours':
          return daysDiff <= 30 && daysDiff > 0;
        default:
          return true;
      }
    })
    .filter((contract) => {
      // Filtre par période d'essai
      if (filterPeriodeEssai === 'all') {
        return true;
      }
      
      if (!contract.periode_essai_mois || !contract.hire_date) {
        return false; // Pas de période d'essai ou date d'embauche
      }
      
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Début de journée
      const hireDate = new Date(contract.hire_date);
      
      // Calcul correct de la date de fin de période d'essai
      const trialEndDate = new Date(hireDate);
      trialEndDate.setMonth(trialEndDate.getMonth() + parseInt(contract.periode_essai_mois));
      
      const daysDiff = Math.ceil((trialEndDate - today) / (1000 * 60 * 60 * 24));
      
      // Debug: afficher les valeurs dans la console
      console.log(`Contrat: ${contract.reference}, Période essai: ${contract.periode_essai_mois} mois, Date embauche: ${hireDate.toISOString().split('T')[0]}, Fin essai: ${trialEndDate.toISOString().split('T')[0]}, Jours restants: ${daysDiff}`);
      
      switch (filterPeriodeEssai) {
        case '10':
          return Math.abs(daysDiff) <= 10; // Inclure les contrats qui expirent dans 10 jours ou qui ont expiré depuis 10 jours
        case '20':
          return Math.abs(daysDiff) <= 20;
        case '30':
          return Math.abs(daysDiff) <= 30;
        default:
          return true;
      }
    })
    .filter((contract) => {
      // Filtre par employé
      if (!filterEmployee) return true;
      return contract.employeeName.toLowerCase().includes(filterEmployee.toLowerCase());
    });

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      // Trier les employés par nom (first_name puis last_name)
      const sortedEmployees = response.data.sort((a, b) => {
        const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setEmployees(sortedEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleRowClick = async (params) => {
    try {
      const response = await api.get(`/contracts/${params.row.id}`);
      setSelectedDetails(response.data);
      setDetailsOpen(true);
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
    }
  };

  const handleAddContract = () => {
    setFormData({
      employee_id: '',
      first_name: '',
      last_name: '',
      date_naissance: '',
      lieu_naissance: '',
      adresse: '',
      situation_familiale: '',
      email: '',
      telephone: '',
      poste: '',
      periode_essai_mois: '',
      renouvelable_fois: '',
      duree_preavis_mois: '',
      salaire: '',
      trial_period_end: '',
      type_contrat: '',
      hire_date: '',
      date_fin_contrat: '',
      motif_fin_contrat: ''
    });
    setSelectedEmployee(null);
    setOpenAddModal(true);
  };

  const handleEmployeeChange = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    setSelectedEmployee(employee);
    
    if (employee) {
      const newFormData = {
        employee_id: employeeId,
        first_name: employee.first_name || '',
        last_name: employee.last_name || '',
        date_naissance: employee.date_naissance ? new Date(employee.date_naissance).toISOString().split('T')[0] : '',
        lieu_naissance: employee.lieu_naissance || '',
        adresse: employee.adresse || '',
        situation_familiale: employee.situation_familiale || '',
        email: employee.email || '',
        telephone: employee.phone || '',
        poste: employee.poste || '',
        periode_essai_mois: employee.periode_essai_mois || '',
        renouvelable_fois: employee.renouvelable_fois || '',
        duree_preavis_mois: employee.duree_preavis_mois || '',
        salaire: employee.salaire ? employee.salaire.toString() : '',
        trial_period_end: employee.trial_period_end ? new Date(employee.trial_period_end).toISOString().split('T')[0] : '',
        type_contrat: employee.type_contrat || '',
        hire_date: employee.hire_date ? new Date(employee.hire_date).toISOString().split('T')[0] : '',
        date_fin_contrat: employee.date_fin_contrat ? new Date(employee.date_fin_contrat).toISOString().split('T')[0] : '',
        motif_fin_contrat: employee.motif_fin_contrat || ''
      };
      
      // Calculer automatiquement la date de fin de période d'essai
      const updatedFormData = updateTrialPeriodEnd(newFormData);
      setFormData(updatedFormData);
    } else {
      const newFormData = { 
        employee_id: employeeId,
        first_name: '',
        last_name: '',
        date_naissance: '',
        lieu_naissance: '',
        adresse: '',
        situation_familiale: '',
        email: '',
        telephone: '',
        poste: '',
        periode_essai_mois: '',
        renouvelable_fois: '',
        duree_preavis_mois: '',
        salaire: '',
        trial_period_end: '',
        type_contrat: '',
        hire_date: '',
        date_fin_contrat: '',
        motif_fin_contrat: ''
      };
      
      // Calculer automatiquement la date de fin de période d'essai
      const updatedFormData = updateTrialPeriodEnd(newFormData);
      setFormData(updatedFormData);
    }
  };

  const handleEditContract = (contract) => {
    setSelectedContract(contract);
    
    // Pré-remplir avec les données du contrat et de l'employé associé
    const employee = contract.employee;
    const newFormData = {
      employee_id: contract.employee_id,
      first_name: employee?.first_name || '',
      last_name: employee?.last_name || '',
      date_naissance: employee?.date_naissance ? new Date(employee.date_naissance).toISOString().split('T')[0] : '',
      lieu_naissance: employee?.lieu_naissance || '',
      adresse: employee?.adresse || '',
      situation_familiale: employee?.situation_familiale || '',
      email: employee?.email || '',
      telephone: employee?.phone || '',
      poste: contract.poste || employee?.poste || '',
      periode_essai_mois: contract.periode_essai_mois || '',
      renouvelable_fois: contract.renouvelable_fois || '',
      duree_preavis_mois: contract.duree_preavis_mois || '',
      salaire: contract.salaire ? contract.salaire.toString() : '',
      trial_period_end: contract.trial_period_end ? new Date(contract.trial_period_end).toISOString().split('T')[0] : '',
      type_contrat: contract.type_contrat || '',
      hire_date: contract.hire_date ? new Date(contract.hire_date).toISOString().split('T')[0] : '',
      date_fin_contrat: contract.date_fin_contrat ? new Date(contract.date_fin_contrat).toISOString().split('T')[0] : '',
      motif_fin_contrat: contract.motif_fin_contrat || '',
      nom_arabic: employee?.nom_arabic || contract?.nom_arabic || '',
      prenom_arabic: employee?.prenom_arabic || contract?.prenom_arabic || '',
      adresse_arabic: employee?.adresse_arabic || contract?.adresse_arabic || ''
    };
    
    // Calculer automatiquement la date de fin de période d'essai
    const updatedFormData = updateTrialPeriodEnd(newFormData);
    setFormData(updatedFormData);
    setOpenEditModal(true);
  };

  const handleDeleteContract = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce contrat ?')) {
      try {
        await api.delete(`/contracts/${id}`);
        fetchContracts();
      } catch (error) {
        console.error('Error deleting contract:', error);
      }
    }
  };

  const handleViewPDF = (contract) => {
    setSelectedContract(contract);
    setOpenPDFModal(true);
  };

  const handleAddSubmit = async () => {
    try {
      await api.post('/contracts', formData);
      setOpenAddModal(false);
      fetchContracts();
    } catch (error) {
      console.error('Error creating contract:', error);
    }
  };

  const handleEditSubmit = async () => {
    try {
      await api.put(`/contracts/${selectedContract.id}`, formData);
      setOpenEditModal(false);
      fetchContracts();
    } catch (error) {
      console.error('Error updating contract:', error);
    }
  };

  const handleExportCSV = () => {
    const rows = filteredContracts;
    if (!rows || rows.length === 0) {
      alert('Aucune donnée à exporter.');
      return;
    }
    const header = [
      'Référence',
      'Employé',
      'Type de contrat',
      'Date d\'embauche',
      'Période d\'essai',
      'PE renouvelable (fois)',
      'Fin période essai',
      'Date fin contrat',
      'Durée préavis',
      'Poste'
    ];
    const escapeCsv = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      if (s.includes('"') || s.includes(',') || s.includes('\n')) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };
    const csv = [header.join(',')]
      .concat(
        rows.map((r) =>
          [
            escapeCsv(r.reference),
            escapeCsv(r.employeeName),
            escapeCsv(r.employee?.type_contrat || ''),
            escapeCsv(r.employee?.hire_date ? new Date(r.employee.hire_date).toLocaleDateString('fr-FR') : ''),
            escapeCsv(r.employee?.periode_essai_mois ? `${r.employee.periode_essai_mois} mois` : ''),
            escapeCsv(r.employee?.renouvelable_fois ? `${r.employee.renouvelable_fois} fois` : ''),
            escapeCsv(r.employee?.trial_period_end ? new Date(r.employee.trial_period_end).toLocaleDateString('fr-FR') : ''),
            escapeCsv(r.employee?.date_fin_contrat ? new Date(r.employee.date_fin_contrat).toLocaleDateString('fr-FR') : ''),
            escapeCsv(r.employee?.duree_preavis_mois ? `${r.employee.duree_preavis_mois} mois` : ''),
            escapeCsv(r.employee?.poste || '')
          ].join(',')
        )
      )
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const now = new Date();
    const ts = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    link.download = `contrats_${ts}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const columns = [
    { field: 'reference', headerName: 'Référence', width: 150, headerClassName: 'super-app-theme--header' },
    { field: 'employeeName', headerName: 'Employé', width: 200, headerClassName: 'super-app-theme--header' },
    { 
      field: 'typeContrat', 
      headerName: 'Type de contrat', 
      width: 200, 
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => params.row.type_contrat || '—'
    },
    { 
      field: 'hireDate', 
      headerName: 'Date d\'embauche', 
      width: 150, 
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => {
        const hireDate = params.row.hire_date;
        return hireDate ? new Date(hireDate).toLocaleDateString('fr-FR') : '—';
      }
    },
    { 
      field: 'periodeEssai', 
      headerName: 'Période d\'essai', 
      width: 120, 
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => {
        const periodeEssai = params.row.periode_essai_mois;
        return periodeEssai ? `${periodeEssai} mois` : '—';
      }
    },
    { 
      field: 'renouvelableFois', 
      headerName: 'PE renouvelable (fois)', 
      width: 150, 
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => {
        const renouvelable = params.row.renouvelable_fois;
        return renouvelable ? `${renouvelable} fois` : '—';
      }
    },
    { 
      field: 'trialPeriodEnd', 
      headerName: 'Fin période essai', 
      width: 150, 
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => {
        const trialPeriodEnd = params.row.trial_period_end;
        return trialPeriodEnd ? new Date(trialPeriodEnd).toLocaleDateString('fr-FR') : '—';
      }
    },
    { 
      field: 'dateFinContrat', 
      headerName: 'Date fin contrat', 
      width: 150, 
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => {
        const dateFinContrat = params.row.date_fin_contrat;
        return dateFinContrat ? new Date(dateFinContrat).toLocaleDateString('fr-FR') : '—';
      }
    },
    { 
      field: 'motifFinContrat', 
      headerName: 'Motif fin contrat', 
      width: 150, 
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => params.row.motif_fin_contrat || '—'
    },
    { 
      field: 'status', 
      headerName: 'Statut', 
      width: 120, 
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => {
        const status = params.row.status;
        return (
          <Box
            sx={{
              backgroundColor: status === 'Actif' ? '#e8f5e8' : '#fff8e1',
              color: status === 'Actif' ? '#2e7d32' : '#f57c00',
              padding: '4px 8px',
              borderRadius: '4px',
              fontWeight: 'bold',
              fontSize: '12px'
            }}
          >
            {status}
          </Box>
        );
      }
    },
    { 
      field: 'dureePreavis', 
      headerName: 'Durée préavis', 
      width: 120, 
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => {
        const dureePreavis = params.row.duree_preavis_mois;
        return dureePreavis ? `${dureePreavis} mois` : '—';
      }
    },
    { 
      field: 'poste', 
      headerName: 'Poste', 
      width: 180, 
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => params.row.poste || '—'
    },
    {
      field: 'salaire',
      headerName: 'Salaire',
      width: 120,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => params.row.salaire ? `${params.row.salaire} DA` : '—'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      filterable: false,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => {
        const userRole = theUser?.role?.toString();

        return (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', py: 1 }}>
            <Tooltip title="Voir PDF">
              <IconButton size="small" onClick={(e) => {
                e.stopPropagation(); // Empêche la propagation du clic
                handleViewPDF(params.row);
              }}>
                <PictureAsPdfIcon sx={{ color: 'blue' }} />
              </IconButton>
            </Tooltip>

            {userRole === "1" && (
              <Tooltip title="Modifier">
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation(); // Empêche la propagation du clic
                  handleEditContract(params.row);
                }}>
                  <EditIcon sx={{ color: 'blue' }} />
                </IconButton>
              </Tooltip>
            )}
            
            {userRole === "1" && (
              <Tooltip title="Supprimer">
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation(); // Empêche la propagation du clic
                  handleDeleteContract(params.row.id);
                }}>
                  <DeleteIcon sx={{ color: 'red' }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ];

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <>
      <h2>Contrats</h2>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddContract}
          style={{ marginRight: 20 }}
        >
          Créer un nouveau contrat
        </Button>
        <Select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          variant="outlined"
          style={{ minWidth: 150 }}
        >
          {filterOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <Select
          value={filterTypeContrat}
          onChange={(e) => setFilterTypeContrat(e.target.value)}
          variant="outlined"
          style={{ minWidth: 150 }}
        >
          {filterTypeContratOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <Select
          value={filterExpiration}
          onChange={(e) => setFilterExpiration(e.target.value)}
          variant="outlined"
          style={{ minWidth: 150 }}
        >
          {filterExpirationOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <Select
          value={filterPeriodeEssai}
          onChange={(e) => setFilterPeriodeEssai(e.target.value)}
          variant="outlined"
          style={{ minWidth: 150 }}
        >
          {filterPeriodeEssaiOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <TextField
          label="Employé"
          value={filterEmployee}
          onChange={(e) => setFilterEmployee(e.target.value)}
          placeholder="Filtrer par employé..."
          variant="outlined"
          style={{ minWidth: 200 }}
        />
        <Button variant="outlined" onClick={handleExportCSV}>Exporter CSV</Button>
      </div>
      <div style={{ height: 600, width: '100%', marginTop: 10 }}>
        <DataGrid
          rows={filteredContracts}
          columns={columns}
          pageSize={10}
          autoPageSize={false}
          disableSelectionOnClick
          getRowHeight={() => 80}
          onRowClick={handleRowClick}
          getRowClassName={(params) =>
            params.row.employee?.date_sortie ? 'inactive-row' : 'active-row'
          }
          sx={{
            '& .super-app-theme--header': {
              backgroundColor: '#1976d2',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
            },
            '& .MuiDataGrid-root': {
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#1976d2',
              fontSize: '14px',
              fontWeight: 600,
              color: 'white',
              borderBottom: '2px solid #1565c0',
            },
            '& .MuiDataGrid-columnHeader': {
              '&:focus': {
                outline: 'none',
              },
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            },
            '& .MuiDataGrid-cell': {
              fontSize: '13px',
              padding: '16px 8px',
              borderBottom: '1px solid #f0f0f0',
              '&:focus': {
                outline: 'none',
              },
            },
            '& .MuiDataGrid-row': {
              '&:nth-of-type(even)': {
                backgroundColor: '#fafafa',
              },
              '&:hover': {
                backgroundColor: '#e3f2fd',
                cursor: 'pointer'
              },
              '&.Mui-selected': {
                backgroundColor: '#e3f2fd',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid #e0e0e0',
              backgroundColor: '#f5f5f5',
            },
            '& .MuiDataGrid-pagination': {
              '& .MuiIconButton-root': {
                borderRadius: '4px',
              },
            },
            '.active-row': { backgroundColor: '#e8f5e8' },
            '.inactive-row': { backgroundColor: '#fff8e1' },
          }}
        />
      </div>

      {/* Add Contract Modal */}
      <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>Ajouter un contrat</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Employé</InputLabel>
                <Select
                  value={formData.employee_id}
                  onChange={(e) => handleEmployeeChange(e.target.value)}
                >
                  {employees.map((employee) => (
                    <MenuItem key={employee.id} value={employee.id}>
                      {employee.first_name} {employee.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Informations personnelles */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Informations personnelles</Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Nom"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                disabled
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Prénom"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                disabled
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="اللقب"
                value={formData.nom_arabic}
                onChange={(e) => setFormData({ ...formData, nom_arabic: e.target.value })}
                inputProps={{ dir: 'rtl', style: { textAlign: 'right' } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="الاسم"
                value={formData.prenom_arabic}
                onChange={(e) => setFormData({ ...formData, prenom_arabic: e.target.value })}
                inputProps={{ dir: 'rtl', style: { textAlign: 'right' } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Date de naissance"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.date_naissance}
                onChange={(e) => setFormData({ ...formData, date_naissance: e.target.value })}
                disabled
              />
            </Grid>            
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Lieu de naissance"
                value={formData.lieu_naissance}
                onChange={(e) => setFormData({ ...formData, lieu_naissance: e.target.value })}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Adresse"
                multiline
                rows={2}
                value={formData.adresse}
                onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="العنوان"
                multiline
                rows={2}
                value={formData.adresse_arabic}
                onChange={(e) => setFormData({ ...formData, adresse_arabic: e.target.value })}
                inputProps={{ dir: 'rtl', style: { textAlign: 'right' } }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Situation familiale</InputLabel>
                <Select
                  value={formData.situation_familiale}
                  onChange={(e) => setFormData({ ...formData, situation_familiale: e.target.value })}
                  disabled
                >
                  <MenuItem value="Célibataire">Célibataire</MenuItem>
                  <MenuItem value="Marié(e)">Marié(e)</MenuItem>
                  <MenuItem value="Divorcé(e)">Divorcé(e)</MenuItem>
                  <MenuItem value="Veuf(ve)">Veuf(ve)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Téléphone"
                value={formData.telephone}
                onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                disabled
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Poste"
                value={formData.poste}
                onChange={(e) => setFormData({ ...formData, poste: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Salaire"
                type="number"
                value={formData.salaire}
                onChange={(e) => setFormData({ ...formData, salaire: e.target.value })}
              />
            </Grid>
            
            {/* Informations contrat */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Informations du contrat</Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Type de contrat"
                value={formData.type_contrat}
                onChange={(e) => setFormData({ ...formData, type_contrat: e.target.value })}
                select
              >
                <MenuItem value="CDI – Contrat à Durée Indéterminée">CDI – Contrat à Durée Indéterminée</MenuItem>
                <MenuItem value="CDD – Contrat à Durée Déterminée">CDD – Contrat à Durée Déterminée</MenuItem>
                <MenuItem value="Stage">Stage</MenuItem>
                <MenuItem value="Intérim">Intérim</MenuItem>
                <MenuItem value="Apprentissage">Apprentissage</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Date d'embauche / début contrat"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.hire_date}
                onChange={(e) => handleHireDateChange(e.target.value)}
              />
            </Grid>
            
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Période d'essai (mois)"
                type="number"
                value={formData.periode_essai_mois}
                onChange={(e) => handlePeriodeEssaiChange(e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Date fin période d'essai"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.trial_period_end}
                disabled
                helperText="Calculée automatiquement"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Renouvelable (fois)"
                type="number"
                value={formData.renouvelable_fois}
                onChange={(e) => setFormData({ ...formData, renouvelable_fois: e.target.value })}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Durée préavis (mois)"
                type="number"
                value={formData.duree_preavis_mois}
                onChange={(e) => setFormData({ ...formData, duree_preavis_mois: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Date fin contrat"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.date_fin_contrat}
                onChange={(e) => setFormData({ ...formData, date_fin_contrat: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Motif fin contrat</InputLabel>
                <Select
                  value={formData.motif_fin_contrat || ''}
                  onChange={(e) => setFormData({ ...formData, motif_fin_contrat: e.target.value })}
                >
                  <MenuItem value="">Sélectionner un motif</MenuItem>
                  <MenuItem value="Démission">Démission</MenuItem>
                  <MenuItem value="Licenciement">Licenciement</MenuItem>
                  <MenuItem value="Fin CDD">Fin CDD</MenuItem>
                  <MenuItem value="Fin période d'essai">Fin période d'essai</MenuItem>
                  <MenuItem value="Retraite">Retraite</MenuItem>
                  <MenuItem value="Abandon de poste">Abandon de poste</MenuItem>
                  <MenuItem value="Décès">Décès</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddModal(false)}>Annuler</Button>
          <Button onClick={handleAddSubmit} variant="contained">Ajouter</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Contract Modal */}
      <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>Modifier un contrat</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Référence"
                value={selectedContract?.reference || ''}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Employé"
                value={selectedContract?.employee ? `${selectedContract.employee.first_name} ${selectedContract.employee.last_name}` : ''}
                disabled
              />
            </Grid>
            
            {/* Informations personnelles */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Informations personnelles</Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Nom"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                disabled
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Prénom"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                disabled
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="اللقب"
                value={formData.nom_arabic}
                onChange={(e) => setFormData({ ...formData, nom_arabic: e.target.value })}
                inputProps={{ dir: 'rtl', style: { textAlign: 'right' } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="الاسم"
                value={formData.prenom_arabic}
                onChange={(e) => setFormData({ ...formData, prenom_arabic: e.target.value })}
                inputProps={{ dir: 'rtl', style: { textAlign: 'right' } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Date de naissance"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.date_naissance}
                onChange={(e) => setFormData({ ...formData, date_naissance: e.target.value })}
                disabled
              />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Lieu de naissance"
                value={formData.lieu_naissance}
                onChange={(e) => setFormData({ ...formData, lieu_naissance: e.target.value })}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Adresse"
                multiline
                rows={2}
                value={formData.adresse}
                onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="العنوان"
                multiline
                rows={2}
                value={formData.adresse_arabic}
                onChange={(e) => setFormData({ ...formData, adresse_arabic: e.target.value })}
                inputProps={{ dir: 'rtl', style: { textAlign: 'right' } }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Situation familiale</InputLabel>
                <Select
                  value={formData.situation_familiale}
                  onChange={(e) => setFormData({ ...formData, situation_familiale: e.target.value })}
                  disabled
                >
                  <MenuItem value="Célibataire">Célibataire</MenuItem>
                  <MenuItem value="Marié(e)">Marié(e)</MenuItem>
                  <MenuItem value="Divorcé(e)">Divorcé(e)</MenuItem>
                  <MenuItem value="Veuf(ve)">Veuf(ve)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Téléphone"
                value={formData.telephone}
                onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                disabled
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Poste"
                value={formData.poste}
                onChange={(e) => setFormData({ ...formData, poste: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Salaire"
                type="number"
                value={formData.salaire}
                onChange={(e) => setFormData({ ...formData, salaire: e.target.value })}
              />
            </Grid>
            
            {/* Informations contrat */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Informations du contrat</Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Type de contrat"
                value={formData.type_contrat}
                onChange={(e) => setFormData({ ...formData, type_contrat: e.target.value })}
                select
              >
                <MenuItem value="CDI – Contrat à Durée Indéterminée">CDI – Contrat à Durée Indéterminée</MenuItem>
                <MenuItem value="CDD – Contrat à Durée Déterminée">CDD – Contrat à Durée Déterminée</MenuItem>
                <MenuItem value="Stage">Stage</MenuItem>
                <MenuItem value="Intérim">Intérim</MenuItem>
                <MenuItem value="Apprentissage">Apprentissage</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Date d'embauche / début contrat"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.hire_date}
                onChange={(e) => handleHireDateChange(e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Date fin période d'essai"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.trial_period_end}
                disabled
                helperText="Calculée automatiquement"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Période d'essai (mois)"
                type="number"
                value={formData.periode_essai_mois}
                onChange={(e) => handlePeriodeEssaiChange(e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Renouvelable (fois)"
                type="number"
                value={formData.renouvelable_fois}
                onChange={(e) => setFormData({ ...formData, renouvelable_fois: e.target.value })}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Durée préavis (mois)"
                type="number"
                value={formData.duree_preavis_mois}
                onChange={(e) => setFormData({ ...formData, duree_preavis_mois: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Date fin contrat"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.date_fin_contrat}
                onChange={(e) => setFormData({ ...formData, date_fin_contrat: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Motif fin contrat</InputLabel>
                <Select
                  value={formData.motif_fin_contrat || ''}
                  onChange={(e) => setFormData({ ...formData, motif_fin_contrat: e.target.value })}
                >
                  <MenuItem value="">Sélectionner un motif</MenuItem>
                  <MenuItem value="Démission">Démission</MenuItem>
                  <MenuItem value="Licenciement">Licenciement</MenuItem>
                  <MenuItem value="Fin CDD">Fin CDD</MenuItem>
                  <MenuItem value="Fin période d'essai">Fin période d'essai</MenuItem>
                  <MenuItem value="Retraite">Retraite</MenuItem>
                  <MenuItem value="Abandon de poste">Abandon de poste</MenuItem>
                  <MenuItem value="Décès">Décès</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditModal(false)}>Annuler</Button>
          <Button onClick={handleEditSubmit} variant="contained">Modifier</Button>
        </DialogActions>
      </Dialog>

      {/* Details Modal */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Détails du contrat</DialogTitle>
        <DialogContent>
          {selectedDetails && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '10px' }}>
              <div><strong>Référence:</strong> {selectedDetails.reference}</div>
              <div><strong>Employé:</strong> {selectedDetails.employee?.first_name} {selectedDetails.employee?.last_name}</div>
              <div><strong>Type de contrat:</strong> {selectedDetails.type_contrat}</div>
              <div><strong>Date d'embauche:</strong> {selectedDetails.hire_date ? new Date(selectedDetails.hire_date).toLocaleDateString() : '—'}</div>
              <div><strong>Période d'essai:</strong> {selectedDetails.periode_essai_mois ? `${selectedDetails.periode_essai_mois} mois` : '—'}</div>
              <div><strong>PE renouvelable:</strong> {selectedDetails.renouvelable_fois ? `${selectedDetails.renouvelable_fois} fois` : '—'}</div>
              <div><strong>Fin période essai:</strong> {selectedDetails.trial_period_end ? new Date(selectedDetails.trial_period_end).toLocaleDateString() : '—'}</div>
              <div><strong>Date fin contrat:</strong> {selectedDetails.date_fin_contrat ? new Date(selectedDetails.date_fin_contrat).toLocaleDateString() : '—'}</div>
              <div><strong>Motif fin contrat:</strong> {selectedDetails.motif_fin_contrat || '—'}</div>
              <div><strong>Durée préavis:</strong> {selectedDetails.duree_preavis_mois ? `${selectedDetails.duree_preavis_mois} mois` : '—'}</div>
              <div><strong>Poste:</strong> {selectedDetails.poste || '—'}</div>
              <div><strong>Salaire:</strong> {selectedDetails.salaire ? `${selectedDetails.salaire} DA` : '—'}</div>
              <div><strong>Statut:</strong> 
                {selectedDetails.date_fin_contrat && new Date(selectedDetails.date_fin_contrat) < new Date() ? 
                  <span style={{ color: 'orange', marginLeft: '8px' }}>⏳ Terminé</span> : 
                  <span style={{ color: 'green', marginLeft: '8px' }}>✓ Actif</span>
                }
              </div>
              <div><strong>Créé le:</strong> {new Date(selectedDetails.createdAt).toLocaleDateString()} à {new Date(selectedDetails.createdAt).toLocaleTimeString()}</div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* PDF Modal */}
      <PDFModalContract
        open={openPDFModal}
        onClose={() => setOpenPDFModal(false)}
        contract={selectedContract}
      />
    </>
  );
};

export default ContractsPage;
